import { ThemeProvider } from 'emotion-theming';
import Menu, { MenuItem } from '@veeva/corgix/menu';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
    searchResultLinkOverrideCSS,
    searchResultsBrowserAllAppFamiliesCSS,
    searchResultsNoResultsFoundCSS,
} from './styles/RevStatsSearchResultsMenu.styles';
import MenuDivider from '../../../common/components/MenuDivider';
import RevStatsSearchResultLink from './RevStatsSearchResultLink';
import getSearchItemUri from '../../services/data/getSearchItemUri';

export default function RevStatsSearchResultsMenu({
    className,
    customers = [],
    products = [],
    focusedValue,
    onMenuItemClick,
    onMenuMapUpdate,
    searchText,
}) {
    const searchWords = searchText?.split(' ') || [];
    const allProductsUrl = '/products';

    const handleMenuItemClick = (_, keyValue) => {
        if (keyValue !== 'not-found') {
            // Only close search results if a real item was clicked (not-found is not a real item)
            onMenuItemClick();
        }
    };

    return (
        // Set padding on the link instead of the MenuItem so the entire MenuItem is a clickable link
        <ThemeProvider theme={{ menuSpacing: 0, menuSpacingVariant1: 0 }}>
            <Menu
                className={className}
                /* UIP-7475 if menu item length doesn't change but the content changes, the menu map won't be updated and keyboard nav stops working */
                key={searchText}
                focusedKeyValue={focusedValue}
                onClick={handleMenuItemClick}
                onMenuMapUpdate={onMenuMapUpdate}
                selectedValue={focusedValue}
            >
                {products.map((p) => {
                    const url = `${getSearchItemUri(p)}`;
                    return (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <RevStatsSearchResultLink
                                category="product"
                                css={searchResultLinkOverrideCSS}
                                label={p.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    );
                })}
                {products?.length > 0 ? (
                    <MenuItem key={allProductsUrl} value={allProductsUrl} keyValue={allProductsUrl}>
                        <Link css={searchResultsBrowserAllAppFamiliesCSS} to={allProductsUrl}>
                            Browse All Products
                        </Link>
                    </MenuItem>
                ) : null}

                {customers.length && products.length ? <MenuDivider /> : null}
                {customers.map((c) => {
                    const url = `/customers/${c.id}`;
                    return (
                        <MenuItem key={url} value={url} keyValue={url}>
                            <RevStatsSearchResultLink
                                category="customer"
                                css={searchResultLinkOverrideCSS}
                                label={c.label}
                                searchWords={searchWords}
                                url={url}
                            />
                        </MenuItem>
                    );
                })}
                {!customers.length && !products.length ? (
                    <MenuItem
                        css={searchResultsNoResultsFoundCSS}
                        value="not-found"
                        keyValue="not-found"
                    >
                        No results found
                    </MenuItem>
                ) : null}
            </Menu>
        </ThemeProvider>
    );
}
RevStatsSearchResultsMenu.propTypes = {
    className: PropTypes.string,
    customers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            label: PropTypes.string,
        }),
    ),
    focusedValue: PropTypes.string,
    onMenuItemClick: PropTypes.func,
    onMenuMapUpdate: PropTypes.func,
    products: PropTypes.arrayOf(
        PropTypes.shape({
            area: PropTypes.number,
            category: PropTypes.number,
            label: PropTypes.string,
            product: PropTypes.number,
            suite: PropTypes.number,
        }),
    ),
    searchText: PropTypes.string,
};
